import { useMemo } from 'react';

import { ButtonVariantPropTypes } from '~/types/mui';

import { BUTTON_VARIANTS } from '../../SSOLink/utils/constants';
import { SsoVariantTypes } from './types';

/**
 * Common hook options used across all SSO components
 * Disables refetch on window focus and requires manual triggering
 */
export const SSO_HOOK_OPTIONS = {
  query: {
    refetchOnWindowFocus: false,
    enabled: false,
  },
};

/**
 * Returns the disabled state for SSO components
 *
 * @param disabled - Explicit disabled prop from user (true/false/undefined)
 * @param contextDisabled - Contextual disabled state from hosting or site status
 * @returns boolean - Final disabled state with user preference taking priority
 */
export const useDisabledState = (
  disabled: boolean | undefined,
  contextDisabled: boolean | undefined,
) => {
  return useMemo(() => {
    if (disabled !== undefined) return disabled;
    return contextDisabled || false;
  }, [disabled, contextDisabled]);
};

/**
 * Generates appropriate test ID for SSO components based on variant and IDs
 *
 * @param explicitTestId - Explicit data-testid from props
 * @param variant - SSO component variant type
 * @param siteId - Site ID if available
 * @param hostingId - Hosting ID if available
 * @param dataTestId - Default test ID fallback
 * @returns string - Generated test ID
 */
export const getTestId = (
  explicitTestId: string | undefined,
  variant: SsoVariantTypes,
  siteId: string | number | undefined,
  hostingId: string | number | undefined,
  dataTestId: string,
): string => {
  if (explicitTestId) {
    return explicitTestId;
  }

  if (variant === 'textLink') {
    return `sso-link-${siteId}`;
  }

  if (BUTTON_VARIANTS.includes(variant as ButtonVariantPropTypes)) {
    // Use siteId if available, otherwise hostingId, otherwise omit suffix
    const id = siteId || hostingId;
    const suffix = id ? `-${id}` : '';
    return `sso-button${suffix}`;
  }

  return dataTestId;
};
